import Button, { LoadingButton } from '@atlaskit/button';
import { RequiredAsterisk } from '@atlaskit/form';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { useAnalytics } from '@databases/analytics';
import DatabasePicker from '@databases/content-picker/DatabasePicker';
import { getBaseUrl } from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import {
  type BrokenDatabase,
  type Database,
  type DatabaseField,
  type EntryCreateOptions,
  type EntryWithContext,
} from '@databases/types/database';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { messages } from '../../i18n';
import DatabaseEntryPicker from '../entryPicker/DatabaseEntryPicker';
import DatabaseValuePicker from '../valuePicker/DatabaseValuePicker';

import { getEmbedUrl } from './embed';
import {
  ReferenceOptionContainer,
  ReferenceOptionHeader,
  ReferencePickerContainer,
  OptionGroup,
  ActionsContainer,
} from './embedReferencePicker.styles';

const pickerStyles = xcss({
  width: '240px',
});

const OptionHeader: FunctionComponent<{ label: string; isRequired?: boolean }> = ({
  label,
  isRequired,
}) => {
  const { formatMessage } = useIntl();

  return (
    <ReferenceOptionContainer>
      <ReferenceOptionHeader>{label}</ReferenceOptionHeader>
      {isRequired ? (
        <RequiredAsterisk />
      ) : (
        <ReferenceOptionHeader isSubtle={true}>
          {formatMessage(messages['reference-picker.optional'])}
        </ReferenceOptionHeader>
      )}
    </ReferenceOptionContainer>
  );
};

export interface EmbedReferencePickerProps {
  databaseError: boolean;
  handleCancel: () => void;
  spaceKey: string;
  selectedDatabase: Database | BrokenDatabase | undefined;
  selectedEntry: EntryWithContext | EntryCreateOptions | undefined;
  selectedField: DatabaseField | undefined;
  setSelectedDatabase: (value: Database | BrokenDatabase | undefined) => void;
  setSelectedEntry: (value: EntryWithContext | EntryCreateOptions | undefined) => void;
  setSelectedField: (value: DatabaseField | undefined) => void;
  isEmptySubmit: boolean;
  setIsEmptySubmit: (isEmptySubmit: boolean) => void;
}

const EmbedReferencePicker: FunctionComponent<EmbedReferencePickerProps> = ({
  databaseError,
  handleCancel,
  selectedDatabase,
  selectedEntry,
  selectedField,
  setSelectedDatabase,
  setSelectedEntry,
  setSelectedField,
  spaceKey,
  isEmptySubmit,
  setIsEmptySubmit,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();

  const baseUrl = getBaseUrl();

  const { formatMessage } = useIntl();
  const analyticsClient = useAnalytics();
  const { view } = useView();

  const [embedLoading, setEmbedLoading] = useState<boolean>(false);

  const isNewEmbedDBModalEnabled = isFeatureFlagEnabled(
    'databases-frontend-embed-database-modal'
  );

  const onDatabaseChange = useCallback(
    (val: Database | BrokenDatabase | undefined) => {
      if (val) {
        setSelectedDatabase(val);
        setIsEmptySubmit(false);
      } else {
        setSelectedDatabase(undefined);
        setIsEmptySubmit(true);
      }

      setSelectedEntry(undefined);
      setSelectedField(undefined);
    },
    [setIsEmptySubmit, setSelectedDatabase, setSelectedEntry, setSelectedField]
  );

  const handleSubmit = useCallback(async () => {
    if (selectedDatabase) {
      setEmbedLoading(true);
      const url = await getEmbedUrl(
        selectedDatabase,
        selectedEntry,
        selectedField,
        dispatch,
        store,
        view,
        analyticsClient,
        spaceKey!,
        baseUrl
      );
      window.parent.postMessage(JSON.stringify(url), window.location.origin);
      setEmbedLoading(false);
    } else {
      setIsEmptySubmit(true);
    }
  }, [
    selectedDatabase,
    selectedEntry,
    selectedField,
    dispatch,
    store,
    view,
    analyticsClient,
    spaceKey,
    baseUrl,
    setIsEmptySubmit,
  ]);

  return isNewEmbedDBModalEnabled ? (
    <Inline
      xcss={xcss({
        gap: 'space.150',
      })}
      alignInline="start"
    >
      <Box xcss={pickerStyles}>
        <DatabasePicker
          onChange={onDatabaseChange}
          selectedDatabase={selectedDatabase}
          isInvalid={databaseError || isEmptySubmit}
        />
      </Box>

      <Box xcss={pickerStyles}>
        <DatabaseEntryPicker
          selectedDatabase={selectedDatabase}
          setSelectedEntry={setSelectedEntry}
          selectedEntry={selectedEntry}
          setSelectedField={setSelectedField}
          canCreate={selectedDatabase ? selectedDatabase.operations.updateEntries : false}
        />
      </Box>

      <Box xcss={pickerStyles}>
        <DatabaseValuePicker
          selectedDatabase={selectedDatabase}
          selectedEntry={selectedEntry}
          selectedField={selectedField}
          setSelectedField={setSelectedField}
        />
      </Box>
    </Inline>
  ) : (
    <ReferencePickerContainer>
      <OptionGroup>
        <OptionHeader
          label={formatMessage(messages['reference-database-picker.label'])}
          isRequired
        />
        <DatabasePicker
          onChange={onDatabaseChange}
          selectedDatabase={selectedDatabase}
          isInvalid={databaseError || isEmptySubmit}
        />
      </OptionGroup>

      <OptionGroup>
        <OptionHeader label={formatMessage(messages['reference-entry-picker.label'])} />
        <DatabaseEntryPicker
          selectedDatabase={selectedDatabase}
          setSelectedEntry={setSelectedEntry}
          selectedEntry={selectedEntry}
          setSelectedField={setSelectedField}
          canCreate={selectedDatabase ? selectedDatabase.operations.updateEntries : false}
        />
      </OptionGroup>

      <OptionGroup>
        <OptionHeader label={formatMessage(messages['reference-value-picker.label'])} />
        <DatabaseValuePicker
          selectedDatabase={selectedDatabase}
          selectedEntry={selectedEntry}
          selectedField={selectedField}
          setSelectedField={setSelectedField}
        />
      </OptionGroup>

      <ActionsContainer>
        <Button
          appearance={'subtle'}
          onClick={handleCancel}
          testId={'reference-cancel-button'}
        >
          {formatMessage(messages['reference-picker.cancel'])}
        </Button>
        <LoadingButton
          testId="reference-submit-button"
          type="submit"
          appearance={'primary'}
          isLoading={embedLoading}
          onClick={handleSubmit}
        >
          {formatMessage(messages['reference-picker.embed'])}
        </LoadingButton>
      </ActionsContainer>
    </ReferencePickerContainer>
  );
};
export default EmbedReferencePicker;
